import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api, type PageResponse } from '@/api/client'
import { Button } from '@/components/primitives'
import {
  Dialog,
  SelectField,
  TextField,
  CheckboxField,
  Notice,
  useApiErrorMessage,
} from '@/components/controls'
import { useT } from '@/i18n/context'
import type { ID, Provider } from '@/types/domain'

interface HostLite {
  id: ID
  name: string
  isControlHost: boolean
}

interface CreatedAccount {
  id: ID
}

interface StartedSession {
  id: ID
}

/**
 * AddAccountDialog registers a provider account. The normal path is a managed
 * account: Kimene creates a fresh, owner-only credential directory and the
 * operator signs in through the cockpit, so no credential is ever imported or
 * typed here. "Create and sign in" launches that sign-in session and hands its
 * id back so the caller can open a pane.
 */
export function AddAccountDialog({
  onClose,
  onSignIn,
}: {
  onClose: () => void
  onSignIn: (sessionId: ID, label: string) => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const apiError = useApiErrorMessage()

  const hosts = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<HostLite>>('/hosts'),
  })

  const controlHost = useMemo(
    () => (hosts.data?.items ?? []).find((h) => h.isControlHost),
    [hosts.data],
  )

  const [provider, setProvider] = useState<Provider>('claude')
  const [label, setLabel] = useState('')
  const [managed, setManaged] = useState(true)
  const [profileDir, setProfileDir] = useState('')
  // The account's credentials — and the sessions that use them — live on this
  // host. It defaults to the control host but the operator can pick another; a
  // managed account then has its directory created, and signs in, on that host.
  const [hostId, setHostId] = useState('')
  useEffect(() => {
    if (hostId === '' && controlHost) setHostId(controlHost.id)
  }, [controlHost, hostId])
  // Codex's device-code sign-in, for a headless host with no local browser. Only
  // Codex has it, and only a managed account signs in through the cockpit.
  const [deviceAuth, setDeviceAuth] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      api.post<CreatedAccount>('/accounts', {
        provider,
        label: label.trim(),
        hostId: hostId || controlHost?.id,
        managed,
        profileDir: managed ? undefined : profileDir.trim() || undefined,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['accounts'] }),
  })

  const useDeviceAuth = deviceAuth && provider === 'codex' && managed
  const login = useMutation({
    mutationFn: (accountId: ID) =>
      api.post<StartedSession>(
        `/accounts/${accountId}/login${useDeviceAuth ? '?deviceAuth=1' : ''}`,
      ),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
      onSignIn(session.id, label.trim())
    },
  })

  const canSubmit =
    label.trim() !== '' &&
    hostId !== '' &&
    (managed || profileDir.trim() !== '') &&
    !create.isPending &&
    !login.isPending

  const submit = async (thenSignIn: boolean) => {
    const account = await create.mutateAsync()
    if (thenSignIn) {
      login.mutate(account.id)
    } else {
      onClose()
    }
  }

  return (
    <Dialog
      title={t('account.addTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={() => void submit(false)}
            disabled={!canSubmit}
            busy={create.isPending && !login.isPending}
          >
            {t('account.addSubmit')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit(true)}
            disabled={!canSubmit}
            busy={login.isPending}
          >
            {t('account.addThenSignIn')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <SelectField
          label={t('account.addProvider')}
          value={provider}
          onChange={(v) => setProvider(v as Provider)}
          options={[
            { value: 'claude', label: 'Claude' },
            { value: 'codex', label: 'Codex' },
            { value: 'generic', label: 'Generic' },
          ]}
        />

        <TextField
          label={t('account.addLabel')}
          value={label}
          onChange={setLabel}
          placeholder={t('account.addLabelHint')}
        />

        <SelectField
          label={t('account.host')}
          value={hostId}
          onChange={setHostId}
          hint={t('account.addHostHint')}
          options={(hosts.data?.items ?? []).map((h) => ({
            value: h.id,
            label: h.isControlHost ? `${h.name} · ${t('account.controlHostTag')}` : h.name,
          }))}
        />

        <CheckboxField
          label={t('account.addManaged')}
          hint={t('account.addManagedHint')}
          checked={managed}
          onChange={setManaged}
        />

        {!managed && (
          <TextField
            label={t('account.addProfileDir')}
            value={profileDir}
            onChange={setProfileDir}
          />
        )}

        {provider === 'codex' && managed && (
          <CheckboxField
            label={t('account.deviceAuth')}
            hint={t('account.deviceAuthHint')}
            checked={deviceAuth}
            onChange={setDeviceAuth}
          />
        )}

        {(create.error || login.error) && (
          <Notice tone="err">{apiError(create.error ?? login.error).title}</Notice>
        )}
      </div>
    </Dialog>
  )
}
